import { useId, type ReactElement } from 'react'
import type { JotformField } from '@ft/shared'

export type MealFieldValue = string | readonly string[]

export function MealFieldEditor({
  fields,
  values,
  onChange,
}: {
  readonly fields: readonly JotformField[]
  readonly values: Readonly<Record<string, MealFieldValue>>
  readonly onChange: (qid: string, value: string | string[]) => void
}): ReactElement {
  const formId = useId()

  return (
    <>
      {fields.map((field) => {
        const current = values[field.qid] ?? (field.type === 'checkbox' ? [] : '')
        if (field.type === 'checkbox') {
          const selected = Array.isArray(current) ? current : []
          return (
            <fieldset key={field.qid} className="field" style={{ margin: 0 }}>
              <legend>{field.label}</legend>
              <div style={{ display: 'grid', gap: '7px' }}>
                {(field.options ?? []).map((option) => (
                  <label key={option} style={{ display: 'flex', gap: '8px', alignItems: 'start' }}>
                    <input
                      type="checkbox"
                      data-qid={field.qid}
                      value={option}
                      checked={selected.includes(option)}
                      onChange={(event) => onChange(
                        field.qid,
                        event.target.checked
                          ? [...selected, option]
                          : selected.filter((value) => value !== option)
                      )}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )
        }
        if (field.type === 'radio') {
          return (
            <fieldset key={field.qid} className="field" style={{ margin: 0 }}>
              <legend>{field.label}</legend>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {(field.options ?? []).map((option) => (
                  <label key={option} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="radio"
                      name={`${formId}-${field.qid}`}
                      data-qid={field.qid}
                      value={option}
                      checked={current === option}
                      onChange={() => onChange(field.qid, option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )
        }
        if (field.type === 'dropdown') {
          return (
            <label key={field.qid} className="field">
              <span>{field.label}</span>
              <select
                className="input"
                data-qid={field.qid}
                value={typeof current === 'string' ? current : ''}
                onChange={(event) => onChange(field.qid, event.target.value)}
              >
                <option value="">Choose…</option>
                {(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
          )
        }
        if (field.type === 'textarea') {
          return (
            <label key={field.qid} className="field">
              <span>{field.label}</span>
              <textarea
                className="input"
                data-qid={field.qid}
                value={typeof current === 'string' ? current : ''}
                onChange={(event) => onChange(field.qid, event.target.value)}
              />
            </label>
          )
        }
        return (
          <label key={field.qid} className="field">
            <span>{field.label}</span>
            <input
              type="text"
              className="input"
              data-qid={field.qid}
              value={typeof current === 'string' ? current : ''}
              onChange={(event) => onChange(field.qid, event.target.value)}
            />
          </label>
        )
      })}
    </>
  )
}
